// src/components/admin/AddRsvpModal.tsx
// Manual RSVP entry + edit modal. Admin + Editor.
//   - Add mode  (no `existing`): POST /api/manage/rsvp — writes a verified
//     guest-style doc directly (no Turnstile, no magic-link email).
//   - Edit mode (`existing` set): PUT /api/manage/rsvp/{id} (issue #14) —
//     updates any entry by id (guest or employee). Identity (name/email/employee
//     flag) and attending status are all editable.
// Field gating mirrors the public form: t-shirt sizes only when the event has
// them enabled AND the person is a WIC employee; golf only when enabled.

import { useState } from "react";

const ADULT_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"] as const;
const YOUTH_SIZES = ["Y-XS", "Y-S", "Y-M", "Y-L", "Y-XL"] as const;
const MAX_PARTY = 10;

interface AdultRow {
  name: string;
  golfing: boolean;
  tshirtSize: string;
}
interface ChildRow {
  name: string;
  tshirtSize: string;
}

interface AttendeeLike {
  name?: string;
  type: "adult" | "child";
  tshirtSize?: string;
  golfing?: boolean;
}

// The subset of an RSVP row the modal needs to prefill an edit.
export interface EditableRsvp {
  id: string;
  source: "employee" | "guest";
  displayName?: string;
  name?: string;
  email?: string;
  upn?: string;
  isEmployee?: boolean;
  attending?: boolean;
  partySize: number;
  adultsCount?: number;
  childrenCount?: number;
  guestNames?: string[];
  tshirtSizes?: string[] | { adult?: string[]; youth?: string[] };
  golfPlaying?: boolean;
  attendees?: AttendeeLike[];
  dietaryNotes?: string;
}

interface Props {
  golfEnabled: boolean;
  tshirtsEnabled: boolean;
  getApiToken: () => Promise<string | null>;
  onClose: () => void;
  onSaved: () => void;
  existing?: EditableRsvp;
}

// Build adult/child editor rows from an existing doc — prefers attendees[],
// falls back to legacy counts + parallel arrays (best-effort attribution).
function rowsFromExisting(e: EditableRsvp): { adults: AdultRow[]; children: ChildRow[] } {
  const adults: AdultRow[] = [];
  const children: ChildRow[] = [];

  if (Array.isArray(e.attendees) && e.attendees.length > 0) {
    for (const a of e.attendees) {
      if (a.type === "child") {
        children.push({ name: a.name ?? "", tshirtSize: a.tshirtSize ?? "" });
      } else {
        adults.push({ name: a.name ?? "", golfing: a.golfing === true, tshirtSize: a.tshirtSize ?? "" });
      }
    }
  } else {
    const nAdults = e.adultsCount ?? Math.max(1, (e.partySize ?? 1) - (e.childrenCount ?? 0));
    const nChildren = e.childrenCount ?? 0;
    const names = [e.displayName ?? e.name ?? "", ...(e.guestNames ?? [])];
    const adultSizes = Array.isArray(e.tshirtSizes) ? e.tshirtSizes : e.tshirtSizes?.adult ?? [];
    const youthSizes = Array.isArray(e.tshirtSizes) ? [] : e.tshirtSizes?.youth ?? [];
    for (let i = 0; i < nAdults; i++) {
      adults.push({
        name: names[i] ?? "",
        golfing: i === 0 ? e.golfPlaying === true : false, // legacy: can't attribute past adult 1
        tshirtSize: adultSizes[i] ?? "",
      });
    }
    for (let i = 0; i < nChildren; i++) {
      children.push({ name: names[nAdults + i] ?? "", tshirtSize: youthSizes[i] ?? "" });
    }
  }

  if (adults.length === 0) adults.push({ name: "", golfing: false, tshirtSize: "" });
  return { adults, children };
}

export default function AddRsvpModal({ golfEnabled, tshirtsEnabled, getApiToken, onClose, onSaved, existing }: Props) {
  const isEdit = !!existing;
  const isEmployeeRecord = existing?.source === "employee";
  const initialRows = existing ? rowsFromExisting(existing) : null;

  const [displayName, setDisplayName] = useState(existing?.displayName ?? existing?.name ?? "");
  const [email, setEmail] = useState(existing?.email ?? existing?.upn ?? "");
  const [isEmployee, setIsEmployee] = useState(existing?.isEmployee ?? existing?.source === "employee");
  const [attending, setAttending] = useState(existing?.attending !== false);
  const [adults, setAdults] = useState<AdultRow[]>(initialRows?.adults ?? [{ name: "", golfing: false, tshirtSize: "" }]);
  const [children, setChildren] = useState<ChildRow[]>(initialRows?.children ?? []);
  const [dietaryNotes, setDietaryNotes] = useState(existing?.dietaryNotes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showTshirts = tshirtsEnabled && isEmployee;
  const partySize = adults.length + children.length;

  function resizeAdults(count: number) {
    const n = Math.max(1, Math.min(count, MAX_PARTY - children.length));
    setAdults(prev =>
      Array.from({ length: n }, (_, i) => prev[i] ?? { name: "", golfing: false, tshirtSize: "" })
    );
  }
  function resizeChildren(count: number) {
    const n = Math.max(0, Math.min(count, MAX_PARTY - adults.length));
    setChildren(prev => Array.from({ length: n }, (_, i) => prev[i] ?? { name: "", tshirtSize: "" }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!displayName.trim()) { setError("Name is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError("A valid email is required."); return; }

    // A decline (edit mode only) carries no party — send identity + attending:false.
    const declining = isEdit && !attending;

    const body: Record<string, unknown> = {
      email: email.trim(),
      displayName: displayName.trim(),
      isEmployee,
    };

    if (declining) {
      body.attending = false;
    } else {
      const attendees = [
        ...adults.map((a, i) => ({
          // Adult 1 is the primary contact — default to the RSVP name when blank
          name: (a.name.trim() || (i === 0 ? displayName.trim() : "")),
          type: "adult" as const,
          ...(showTshirts && a.tshirtSize ? { tshirtSize: a.tshirtSize } : {}),
          ...(golfEnabled ? { golfing: a.golfing } : {}),
        })),
        ...children.map(c => ({
          name: c.name.trim(),
          type: "child" as const,
          ...(showTshirts && c.tshirtSize ? { tshirtSize: c.tshirtSize } : {}),
        })),
      ];
      if (isEdit) body.attending = true;
      body.partySize = partySize;
      body.adultsCount = adults.length;
      body.childrenCount = children.length;
      body.attendees = attendees;
      body.dietaryNotes = dietaryNotes.trim() || undefined;
    }

    setSaving(true);
    try {
      const token = await getApiToken();
      if (!token) { setError("Your session expired — sign in again."); return; }
      const url = isEdit
        ? `/api/manage/rsvp/${encodeURIComponent(existing!.id)}?year=2026`
        : "/api/manage/rsvp?year=2026";
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        onSaved();
        onClose();
      } else {
        const b = await res.json().catch(() => ({} as { message?: string }));
        setError(b?.message ?? `Save failed (${res.status}).`);
      }
    } catch {
      setError("Save failed. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  const showParty = !isEdit || attending;
  const saveLabel = saving
    ? "Saving…"
    : isEdit
      ? (attending ? `Save Changes (${partySize})` : "Save Changes")
      : `Save RSVP (${partySize})`;

  return (
    <>
      <style>{`
        .arm-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.55); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 4rem 1rem 2rem; overflow-y: auto; }
        .arm-panel { background: var(--white); width: 100%; max-width: 560px; border-top: 3px solid var(--red); padding: 2rem 2.25rem 2.25rem; }
        .arm-panel h2 { font-family: var(--font-display); font-size: 1.25rem; font-weight: 700; text-transform: uppercase; margin: 0 0 1.5rem; color: var(--black); }
        .arm-field { margin-bottom: 1rem; }
        .arm-label { display: block; font-family: var(--font-display); font-size: 0.65rem; font-weight: 600; letter-spacing: 0.15em; text-transform: uppercase; color: var(--muted); margin-bottom: 0.35rem; }
        .arm-input, .arm-select, .arm-textarea { width: 100%; font-family: var(--font-body); font-size: 0.9rem; padding: 0.55rem 0.7rem; border: 1px solid var(--border); background: var(--white); color: var(--black); box-sizing: border-box; }
        .arm-input:focus, .arm-select:focus, .arm-textarea:focus { outline: none; border-color: var(--red); }
        .arm-input:disabled { background: var(--bg); color: var(--muted); cursor: not-allowed; }
        .arm-row { display: flex; gap: 1rem; }
        .arm-row .arm-field { flex: 1; }
        .arm-check { display: flex; align-items: center; gap: 0.5rem; font-family: var(--font-body); font-size: 0.88rem; color: var(--steel); cursor: pointer; }
        .arm-attendee-row { display: flex; gap: 0.6rem; align-items: center; margin-bottom: 0.5rem; }
        .arm-attendee-row .arm-input { flex: 1; }
        .arm-attendee-row .arm-select { width: auto; }
        .arm-golf-check { display: flex; align-items: center; gap: 0.3rem; font-family: var(--font-body); font-size: 0.82rem; color: var(--steel); white-space: nowrap; cursor: pointer; }
        .arm-section-label { font-family: var(--font-display); font-size: 0.7rem; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: var(--black); margin: 1.25rem 0 0.6rem; }
        .arm-error { background: #fdecea; color: #b71c1c; font-family: var(--font-body); font-size: 0.85rem; padding: 0.6rem 0.8rem; margin-bottom: 1rem; }
        .arm-actions { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1.5rem; }
        .arm-cancel { font-family: var(--font-display); font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; padding: 0.6rem 1.25rem; background: var(--white); border: 1px solid var(--border); color: var(--mid); cursor: pointer; }
        .arm-cancel:hover { border-color: var(--mid); }
        .arm-save { font-family: var(--font-display); font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; padding: 0.6rem 1.5rem; background: var(--red); border: none; color: var(--white); cursor: pointer; }
        .arm-save:disabled { background: var(--muted); cursor: wait; }
        .arm-decline-note { font-family: var(--font-body); font-size: 0.85rem; color: var(--muted); padding: 0.75rem 0; }
      `}</style>

      <div className="arm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="arm-panel">
          <h2>{isEdit ? "Edit RSVP" : "Add RSVP"}</h2>
          <form onSubmit={handleSubmit}>
            {error && <div className="arm-error">{error}</div>}

            <div className="arm-row">
              <div className="arm-field">
                <label className="arm-label" htmlFor="arm-name">Name</label>
                <input id="arm-name" className="arm-input" value={displayName} maxLength={120}
                  onChange={e => setDisplayName(e.target.value)} autoFocus />
              </div>
              <div className="arm-field">
                <label className="arm-label" htmlFor="arm-email">Email</label>
                <input id="arm-email" className="arm-input" type="email" value={email} maxLength={254}
                  onChange={e => setEmail(e.target.value)} />
              </div>
            </div>

            <div className="arm-field">
              <label className="arm-check">
                <input type="checkbox" checked={isEmployee} disabled={isEmployeeRecord}
                  onChange={e => setIsEmployee(e.target.checked)} />
                WIC employee{tshirtsEnabled ? " (t-shirt eligible)" : ""}
                {isEmployeeRecord && " — signed in via SSO"}
              </label>
            </div>

            {isEdit && (
              <div className="arm-field">
                <label className="arm-check">
                  <input type="checkbox" checked={attending} onChange={e => setAttending(e.target.checked)} />
                  Attending
                </label>
              </div>
            )}

            {!showParty ? (
              <p className="arm-decline-note">
                Marked as <strong>declined</strong>. Party details are cleared; re-check “Attending” to restore them.
              </p>
            ) : (
              <>
                <div className="arm-row">
                  <div className="arm-field">
                    <label className="arm-label" htmlFor="arm-adults">Adults</label>
                    <input id="arm-adults" className="arm-input" type="number" min={1} max={MAX_PARTY}
                      value={adults.length} onChange={e => resizeAdults(parseInt(e.target.value || "1", 10))} />
                  </div>
                  <div className="arm-field">
                    <label className="arm-label" htmlFor="arm-children">Children</label>
                    <input id="arm-children" className="arm-input" type="number" min={0} max={MAX_PARTY - 1}
                      value={children.length} onChange={e => resizeChildren(parseInt(e.target.value || "0", 10))} />
                  </div>
                </div>

                <p className="arm-section-label">Adults</p>
                {adults.map((a, i) => (
                  <div className="arm-attendee-row" key={`a${i}`}>
                    <input
                      className="arm-input"
                      placeholder={i === 0 ? "Adult 1 (defaults to name above)" : `Adult ${i + 1}`}
                      value={a.name}
                      maxLength={120}
                      onChange={e => setAdults(prev => prev.map((row, j) => j === i ? { ...row, name: e.target.value } : row))}
                    />
                    {showTshirts && (
                      <select
                        className="arm-select"
                        value={a.tshirtSize}
                        onChange={e => setAdults(prev => prev.map((row, j) => j === i ? { ...row, tshirtSize: e.target.value } : row))}
                      >
                        <option value="">Shirt —</option>
                        {ADULT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                    {golfEnabled && (
                      <label className="arm-golf-check">
                        <input
                          type="checkbox"
                          checked={a.golfing}
                          onChange={e => setAdults(prev => prev.map((row, j) => j === i ? { ...row, golfing: e.target.checked } : row))}
                        />
                        ⛳ Golf
                      </label>
                    )}
                  </div>
                ))}

                {children.length > 0 && (
                  <>
                    <p className="arm-section-label">Children</p>
                    {children.map((c, i) => (
                      <div className="arm-attendee-row" key={`c${i}`}>
                        <input
                          className="arm-input"
                          placeholder={`Child ${i + 1}`}
                          value={c.name}
                          maxLength={120}
                          onChange={e => setChildren(prev => prev.map((row, j) => j === i ? { ...row, name: e.target.value } : row))}
                        />
                        {showTshirts && (
                          <select
                            className="arm-select"
                            value={c.tshirtSize}
                            onChange={e => setChildren(prev => prev.map((row, j) => j === i ? { ...row, tshirtSize: e.target.value } : row))}
                          >
                            <option value="">Shirt —</option>
                            {YOUTH_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                      </div>
                    ))}
                  </>
                )}

                <div className="arm-field" style={{ marginTop: "1rem" }}>
                  <label className="arm-label" htmlFor="arm-dietary">Dietary Notes (optional)</label>
                  <textarea id="arm-dietary" className="arm-textarea" rows={2} maxLength={1000}
                    value={dietaryNotes} onChange={e => setDietaryNotes(e.target.value)} />
                </div>
              </>
            )}

            <div className="arm-actions">
              <button type="button" className="arm-cancel" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="submit" className="arm-save" disabled={saving}>
                {saveLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
